// client/src/hooks/useHeartbeat.js
// The app-wide 15-second "has anything changed?" check (spec §6).
//
// ONE timer per tab (HeartbeatProvider, mounted in App) calls GET /api/diHeartbeat
// with the union of what the mounted pages care about. A page subscribes with
// useHeartbeat({ sections, period, datasets, onChange }) and is called back when:
//   * someone ELSE wrote an audit_log row in one of its sections (same period), or
//     an NLC mark anywhere (it removes the client from every section);
//   * a shared snapshot feeding it was rebuilt (version changed);
//   * the change list overflowed (truncated) — just refetch.
// It also returns the snapshot freshness ({ builtAt, refreshing }) for the
// "Data as of …" stamp. Paused while the tab is hidden; runs at once when it returns.
// It always runs (even with no page subscribed) to watch the period / section
// settings: when the server's configVersion changes, the app re-reads diConfig
// silently, so an Admin change reaches every open tab within one heartbeat.

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/AuthContext';

export const HEARTBEAT_MS = 15 * 1000;

const HeartbeatContext = createContext(null);

const datasetKey = (d) => `${d.name}${d.params ? '.' + Object.keys(d.params).sort().map((k) => d.params[k]).join('.') : ''}`;

export function HeartbeatProvider({ children }) {
  const { reload: reloadAppConfig } = useAuth();
  const reloadRef = useRef(reloadAppConfig);
  reloadRef.current = reloadAppConfig;
  const configVersion = useRef(null);
  const subs = useRef(new Map());        // id → { sections:Set, period, datasets:[], onChange, onFreshness }
  const cursor = useRef(0);
  const versions = useRef(new Map());    // 's:<section>' | 'd:<datasetKey>' → version
  const lastInfo = useRef({ snapshots: {}, datasets: {} });
  const inflight = useRef(false);
  const nextId = useRef(1);

  const freshnessFor = useCallback((sub, info) => {
    const parts = [
      ...[...sub.sections].map((s) => info.snapshots?.[s]),
      ...sub.datasets.map((d) => info.datasets?.[datasetKey(d)]),
    ].filter(Boolean);
    if (!parts.length) return null;
    const built = parts.map((p) => p.builtAt).filter(Boolean).sort();
    return {
      builtAt: built[0] || null,
      refreshing: parts.some((p) => p.refreshing),
      active: parts.some((p) => p.active !== false),
    };
  }, []);

  const tick = useCallback(async () => {
    if (inflight.current) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    inflight.current = true;
    try {
      const sections = new Set();
      const datasets = new Map();
      for (const s of subs.current.values()) {
        s.sections.forEach((k) => sections.add(k));
        s.datasets.forEach((d) => datasets.set(datasetKey(d), d));
      }
      const qs = new URLSearchParams({ since: String(cursor.current) });
      if (sections.size) qs.set('sections', [...sections].join(','));
      if (datasets.size) qs.set('datasets', JSON.stringify([...datasets.values()]));
      const res = await fetch(`/api/diHeartbeat?${qs}`, { credentials: 'include' });
      if (!res.ok) return;
      const hb = await res.json();

      const first = cursor.current === 0;
      cursor.current = Math.max(cursor.current, Number(hb.latestAuditId) || 0);

      // Period / section settings changed (Admin) → re-read diConfig in the background.
      if (hb.configVersion) {
        if (configVersion.current && configVersion.current !== hb.configVersion) reloadRef.current({ silent: true });
        configVersion.current = hb.configVersion;
      }
      lastInfo.current = { snapshots: hb.snapshots || {}, datasets: hb.datasets || {} };

      // Which versions moved since the last tick (ignoring first sightings).
      const moved = new Set();
      const note = (k, v) => {
        if (!v) return;
        const prev = versions.current.get(k);
        if (prev !== undefined && prev !== v) moved.add(k);
        versions.current.set(k, v);
      };
      Object.entries(hb.snapshots || {}).forEach(([k, v]) => note(`s:${k}`, v && v.version));
      Object.entries(hb.datasets || {}).forEach(([k, v]) => note(`d:${k}`, v && v.version));

      for (const s of subs.current.values()) {
        s.onFreshness?.(freshnessFor(s, lastInfo.current));
        if (first) continue;
        const changes = (hb.changes || []).filter((c) =>
          c.global || (s.sections.has(c.sectionKey) && (!s.period || c.period === s.period)));
        const versionChanged = [...s.sections].some((k) => moved.has(`s:${k}`))
          || s.datasets.some((d) => moved.has(`d:${datasetKey(d)}`));
        if (hb.truncated || changes.length || versionChanged) {
          s.onChange?.({ changes, versionChanged, truncated: !!hb.truncated });
        }
      }
    } catch {
      // A missed heartbeat is harmless — the next one catches up from the cursor.
    } finally {
      inflight.current = false;
    }
  }, [freshnessFor]);

  useEffect(() => {
    const id = setInterval(tick, HEARTBEAT_MS);
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
  }, [tick]);

  const subscribe = useCallback((sub) => {
    const id = nextId.current++;
    subs.current.set(id, sub);
    // Serve the freshness we already know, then check right away so a newly
    // mounted page learns its snapshot versions without waiting 15 s.
    sub.onFreshness?.(freshnessFor(sub, lastInfo.current));
    setTimeout(tick, 0);
    return () => { subs.current.delete(id); };
  }, [tick, freshnessFor]);

  return <HeartbeatContext.Provider value={subscribe}>{children}</HeartbeatContext.Provider>;
}

/**
 * Subscribe the current page to the heartbeat.
 * @param {object|null} opts  null/undefined disables (e.g. the spotlight-tour demo)
 * @param {string[]} [opts.sections]  section_keys whose changes/snapshots matter
 * @param {string}   [opts.period]    only changes in this period (NLC marks always count)
 * @param {{name:string, params?:object}[]} [opts.datasets]  explicit snapshot variants
 * @param {(e:{changes:object[], versionChanged:boolean, truncated:boolean}) => void} [opts.onChange]
 * @returns {{ builtAt: string|null, refreshing: boolean, active: boolean } | null} snapshot freshness
 */
export function useHeartbeat(opts) {
  const subscribe = useContext(HeartbeatContext);
  const [freshness, setFreshness] = useState(null);
  const onChangeRef = useRef(opts && opts.onChange);
  onChangeRef.current = opts && opts.onChange;

  const enabled = !!(subscribe && opts);
  const sectionsKey = enabled ? (opts.sections || []).join(',') : '';
  const datasetsKey = enabled ? JSON.stringify(opts.datasets || []) : '';
  const period = enabled ? opts.period || null : null;

  useEffect(() => {
    if (!enabled) return undefined;
    return subscribe({
      sections: new Set(sectionsKey ? sectionsKey.split(',') : []),
      datasets: JSON.parse(datasetsKey || '[]'),
      period,
      onChange: (e) => onChangeRef.current?.(e),
      onFreshness: setFreshness,
    });
  }, [enabled, subscribe, sectionsKey, datasetsKey, period]);

  return enabled ? freshness : null;
}
